import { useTranslation } from 'i18next-vue';
import { computed, onUnmounted, ref, watch, type Ref } from 'vue';

import {
  useActiveIdentity,
  useAuthStore,
  useAuthSessionStore,
  useHubspotChat,
} from '@peanuds/app-shell';
import { useApiRequest } from '@peanuds/composables';
import {
  getApiErrorMessage,
  hasApiErrorCode,
  hasApiErrorStatus,
  isMfaRequiredSentinelError,
  isPendingLivenessCheckSentinelError,
  peanudsApi,
  zod,
  type InitiateEmailChangeChallengeData,
  type Options,
  type ResendEmailChallengeData,
  type VerifyEmailChangeChallengeData,
} from '@peanuds/shared';

type EmailVerificationStep = 'currentEmail' | 'otp' | 'updateEmail';
type OtpVerificationStatus = 'idle' | 'verifying' | 'verified';
type JwtPayload = Record<string, unknown>;

const OTP_LENGTH = 6;
const EMAIL_CLAIM = 'email';
const EMAIL_ERROR_CODES = [
  'CHALLENGES.EMAIL_CHANGE_ALREADY_IN_USE',
  'CHALLENGES.EMAIL_CHANGE_INVALID_OTP',
  'CHALLENGES.EMAIL_CHANGE_NOT_FOUND',
  'CHALLENGES.EMAIL_ALREADY_VERIFIED',
  'CHALLENGES.EMAIL_SEND_FAILED',
  'GENERIC.REQUEST_LOCKED',
] as const;

const parseTokenPayload = (token: string | null): JwtPayload | null => {
  try {
    const encodedPayload = token?.split('.')[1];

    if (!encodedPayload) {
      return null;
    }

    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload: unknown = JSON.parse(atob(padded));

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }

    return payload as JwtPayload;
  } catch {
    return null;
  }
};

const getEmailFromToken = (token: string | null): string | null => {
  const email = parseTokenPayload(token)?.[EMAIL_CLAIM];

  return typeof email === 'string' && email.length > 0 ? email : null;
};

export const useTotalBalanceEmailVerificationDialog = (visible: Ref<boolean>) => {
  const { t } = useTranslation();
  const activeIdentity = useActiveIdentity();
  const authStore = useAuthStore();
  const authSessionStore = useAuthSessionStore();
  const { openChat } = useHubspotChat();

  const {
    execute: executeInitiateEmailChange,
    invalidate: invalidateInitiateEmailChange,
    isLoading: isInitiatingEmailChange,
    isSuccess: isInitiateEmailChangeSuccess,
    error: initiateEmailChangeError,
    reset: resetInitiateEmailChange,
  } = useApiRequest({
    request: (options: Options<InitiateEmailChangeChallengeData>) =>
      peanudsApi.initiateEmailChangeChallenge(options),
    concurrency: { strategy: 'latest-wins' },
    showErrorToast: false,
  });
  const {
    execute: executeVerifyEmailChange,
    invalidate: invalidateVerifyEmailChange,
    isLoading: isVerifyingEmailChange,
    isSuccess: isVerifyEmailChangeSuccess,
    error: verifyEmailChangeError,
    reset: resetVerifyEmailChange,
  } = useApiRequest({
    request: (options: Options<VerifyEmailChangeChallengeData>) =>
      peanudsApi.verifyEmailChangeChallenge(options),
    concurrency: { strategy: 'latest-wins' },
    showErrorToast: false,
  });
  const {
    execute: executeResendEmail,
    invalidate: invalidateResendEmail,
    isLoading: isResendingEmail,
    isSuccess: isResendEmailSuccess,
    error: resendEmailError,
    reset: resetResendEmail,
  } = useApiRequest({
    request: (options: Options<ResendEmailChallengeData>) =>
      peanudsApi.resendEmailChallenge(options),
    concurrency: { strategy: 'latest-wins' },
    showErrorToast: false,
  });

  const step = ref<EmailVerificationStep>('currentEmail');
  const otpValue = ref('');
  const emailDraft = ref('');
  const pendingEmail = ref<string | null>(null);
  const otpVerificationStatus = ref<OtpVerificationStatus>('idle');
  const errorMessage = ref<string | null>(null);
  const otpResetKey = ref(0);
  const resendResetKey = ref(0);
  let dialogLifecycle = 0;

  const currentEmail = computed(() => {
    return activeIdentity.email.value ?? getEmailFromToken(authSessionStore.idToken) ?? '';
  });
  const displayedEmail = computed(() => pendingEmail.value ?? currentEmail.value);
  const normalizedEmail = computed(() => emailDraft.value.trim());
  const isEmailValid = computed(() => {
    return zod.initiateEmailChangeChallengeRequestDto.safeParse({
      email: normalizedEmail.value,
    }).success;
  });
  const isSubmitting = computed(() => {
    return isInitiatingEmailChange.value || isVerifyingEmailChange.value || isResendingEmail.value;
  });
  const isOtpInteractionDisabled = computed(() => {
    return isSubmitting.value || otpVerificationStatus.value !== 'idle';
  });
  const isOtpVerificationFinalizing = computed(() => otpVerificationStatus.value !== 'idle');

  const isOtpContinueDisabled = computed(() => {
    return isOtpInteractionDisabled.value || otpValue.value.length !== OTP_LENGTH;
  });

  const isEmailContinueDisabled = computed(() => {
    return (
      isSubmitting.value || !isEmailValid.value || normalizedEmail.value === displayedEmail.value
    );
  });

  const resetOtpEntry = () => {
    otpValue.value = '';
    otpResetKey.value += 1;
  };

  const resetRequests = () => {
    invalidateInitiateEmailChange();
    invalidateVerifyEmailChange();
    invalidateResendEmail();
    resetInitiateEmailChange();
    resetVerifyEmailChange();
    resetResendEmail();
  };

  const resetDialogState = () => {
    dialogLifecycle += 1;
    resetRequests();
    step.value = 'currentEmail';
    pendingEmail.value = null;
    emailDraft.value = currentEmail.value;
    otpVerificationStatus.value = 'idle';
    errorMessage.value = null;
    resetOtpEntry();
    resendResetKey.value += 1;
  };

  const isCurrentDialogLifecycle = (lifecycle: number) => {
    return visible.value && lifecycle === dialogLifecycle;
  };

  const resolveApiErrorMessage = (error: unknown, fallbackKey: string): string | null => {
    if (
      isPendingLivenessCheckSentinelError(error) ||
      isMfaRequiredSentinelError(error) ||
      hasApiErrorCode(error, 'GENERIC.PENDING_LIVENESS_CHECK')
    ) {
      return null;
    }

    const backendMessage = getApiErrorMessage(error);

    if (backendMessage !== null) {
      return backendMessage;
    }

    const errorCode = EMAIL_ERROR_CODES.find((code) => hasApiErrorCode(error, code));

    if (errorCode) {
      const errorKey = errorCode.toLowerCase().replace(/\./g, '_');

      return t(`totalBalance.email_verification.errors.${errorKey}`);
    }

    if (hasApiErrorStatus(error, 429)) {
      return t('totalBalance.email_verification.errors.generic_too_many_requests');
    }

    return t(fallbackKey);
  };

  const openUpdateEmailStep = () => {
    if (isOtpInteractionDisabled.value) {
      return;
    }

    step.value = 'updateEmail';
    emailDraft.value = displayedEmail.value;
    errorMessage.value = null;
  };

  const returnToVerificationStep = () => {
    step.value = pendingEmail.value ? 'otp' : 'currentEmail';
    errorMessage.value = null;
  };

  const sendCurrentEmailVerification = async (failureMessage: string) => {
    const lifecycle = dialogLifecycle;

    errorMessage.value = null;
    await executeResendEmail({});

    if (!isCurrentDialogLifecycle(lifecycle)) {
      return;
    }

    if (!isResendEmailSuccess.value) {
      errorMessage.value = resolveApiErrorMessage(resendEmailError.value, failureMessage);
    }
  };

  const handleOtpContinue = async () => {
    if (isOtpInteractionDisabled.value || otpValue.value.length !== OTP_LENGTH) {
      return;
    }

    otpVerificationStatus.value = 'verifying';
    errorMessage.value = null;
    await executeVerifyEmailChange({ body: { otp: otpValue.value } });

    if (!isVerifyEmailChangeSuccess.value) {
      errorMessage.value = resolveApiErrorMessage(
        verifyEmailChangeError.value,
        'totalBalance.email_verification.invalid_code',
      );
      otpVerificationStatus.value = 'idle';
      return;
    }

    otpVerificationStatus.value = 'verified';
    await authStore.redirectToLogin();
  };

  const handleEmailContinue = async () => {
    if (!isEmailValid.value) {
      errorMessage.value = 'Enter a valid email address.';
      return;
    }

    if (normalizedEmail.value === displayedEmail.value) {
      errorMessage.value = 'Enter a different email address.';
      return;
    }

    const lifecycle = dialogLifecycle;
    const email = normalizedEmail.value;

    errorMessage.value = null;
    await executeInitiateEmailChange({ body: { email } });

    if (!isCurrentDialogLifecycle(lifecycle)) {
      return;
    }

    if (!isInitiateEmailChangeSuccess.value) {
      errorMessage.value = resolveApiErrorMessage(
        initiateEmailChangeError.value,
        'totalBalance.email_verification.send_code_failed',
      );
      return;
    }

    pendingEmail.value = email;
    step.value = 'otp';
    resetOtpEntry();
    resendResetKey.value += 1;
  };

  const handleResend = async () => {
    if (isOtpInteractionDisabled.value) {
      return;
    }

    const lifecycle = dialogLifecycle;
    const email = pendingEmail.value;

    if (!email) {
      await sendCurrentEmailVerification('totalBalance.email_verification.resend_email_failed');
      return;
    }

    errorMessage.value = null;
    await executeInitiateEmailChange({ body: { email } });

    if (!isCurrentDialogLifecycle(lifecycle)) {
      return;
    }

    if (!isInitiateEmailChangeSuccess.value) {
      errorMessage.value = resolveApiErrorMessage(
        initiateEmailChangeError.value,
        'totalBalance.email_verification.resend_code_failed',
      );
      return;
    }

    resetOtpEntry();
  };

  watch(
    visible,
    (isVisible) => {
      if (isOtpVerificationFinalizing.value) {
        return;
      }

      resetDialogState();

      if (isVisible) {
        void sendCurrentEmailVerification('totalBalance.email_verification.send_email_failed');
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    dialogLifecycle += 1;
    invalidateInitiateEmailChange();
    invalidateResendEmail();

    if (!isOtpVerificationFinalizing.value) {
      invalidateVerifyEmailChange();
    }
  });

  watch(otpValue, () => {
    if (step.value === 'otp') {
      errorMessage.value = null;
    }
  });

  watch(emailDraft, () => {
    if (step.value === 'updateEmail') {
      errorMessage.value = null;
    }
  });

  return {
    step,
    otpValue,
    emailDraft,
    displayedEmail,
    otpVerificationStatus,
    isOtpInteractionDisabled,
    isOtpVerificationFinalizing,
    isSubmitting,
    errorMessage,
    otpResetKey,
    resendResetKey,
    isOtpContinueDisabled,
    isEmailContinueDisabled,
    openChat,
    openUpdateEmailStep,
    returnToVerificationStep,
    handleOtpContinue,
    handleEmailContinue,
    handleResend,
  };
};
